import type { TargetHighlight, TargetSourceHighlight } from './targets';
import type { LaunchMode } from './brand-allowances';
import type { ScoreKind } from '../utils/scoreRule';

/**
 * `brand` is the legacy combined Brand Perception audit type. It remains supported
 * for read-only rendering of historical audits; new audits use the three
 * axis-specific types below. See [docs/features/brand-perception.md](../../../docs/features/brand-perception.md).
 */
export type AuditType =
  | 'website'
  | 'earned_media'
  | 'brand'
  | 'brand_narrative'
  | 'brand_authority'
  | 'brand_referral'
  | 'brand_comparison'
  | 'social'
  | 'news_watch';

/** Brand Perception axes surfaced as independent audit types (FOUR axes, Prompt Studio 2026-10-15). */
export const BRAND_AXIS_AUDIT_TYPES = ['brand_narrative', 'brand_authority', 'brand_referral', 'brand_comparison'] as const;
export type BrandAxisAuditType = (typeof BRAND_AXIS_AUDIT_TYPES)[number];

export function isBrandAxisAuditType(t: string): t is BrandAxisAuditType {
  return (BRAND_AXIS_AUDIT_TYPES as readonly string[]).includes(t);
}
export type AuditStatus = 'pending' | 'running' | 'completed' | 'failed';
export type AiEngine = 'chatgpt' | 'gemini' | 'perplexity' | 'claude';
export type Sentiment = 'positive' | 'neutral' | 'negative';
export type CrawlMode = 'sitemap' | 'full';

/**
 * Uniform retention for everything on `{prefix}-audit-results`, engine response
 * text included. Every `ttl` stamped on that table is computed from this.
 *
 * Lock-step Python mirror: `AUDIT_RESULTS_RETENTION_DAYS` in
 * `packages/agent-earned-media/app/earned_media/_common.py` (vendored into the
 * Brand Perception image as `geo_shared/_common.py`). Change both together.
 *
 * ⚠️ The table now has `timeToLiveAttribute: 'ttl'` enabled, so a `ttl` written
 * with a shorter policy really does delete the row. Any back-dated change here
 * must be preceded by `scripts/normalize-audit-results-ttl.ts`.
 */
export const AUDIT_RESULTS_RETENTION_DAYS = 730;

// --- Website audit agent types ---

export type WebsiteAgentType = 'crawl' | 'content_freshness' | 'structural_seo' | 'content_quality';
export type EarnedMediaAgentType = 'earned_media';
/** Legacy combined Brand Perception agent. */
export type BrandPerceptionAgentType = 'brand_perception';
/** Per-axis Brand Perception agents (post-split, four axes). */
export type BrandAxisAgentType = 'brand_narrative' | 'brand_authority' | 'brand_referral' | 'brand_comparison';
export type NewsWatchAgentType = 'news_watch';
export type AllAgentType =
  | WebsiteAgentType
  | EarnedMediaAgentType
  | BrandPerceptionAgentType
  | BrandAxisAgentType
  | NewsWatchAgentType;
export type AgentStatus = 'pending' | 'running' | 'completed' | 'failed';
export type AgentStatuses = Partial<Record<AllAgentType, AgentStatus>>;

/**
 * Website-audit sub-agents that actually run per tier. Mirrors `PHASE_BY_TIER`
 * in packages/agents/src/agents/crawl.py — basic/lite only run content_quality
 * (content_freshness + structural_seo are Pro+). `crawl` always runs (phase 0).
 * Unknown tier falls back to the full roster (fail open, never hide real data).
 */
export const WEBSITE_AGENTS_BY_TIER: Record<string, WebsiteAgentType[]> = {
  basic: ['crawl', 'content_quality'],
  lite: ['crawl', 'content_quality'],
  pro: ['crawl', 'content_freshness', 'structural_seo', 'content_quality'],
  elite: ['crawl', 'content_freshness', 'structural_seo', 'content_quality'],
  enterprise: ['crawl', 'content_freshness', 'structural_seo', 'content_quality'],
  agency: ['crawl', 'content_freshness', 'structural_seo', 'content_quality'],
};

export function websiteAgentsForTier(tier: string | undefined): WebsiteAgentType[] {
  return WEBSITE_AGENTS_BY_TIER[tier ?? ''] ?? ['crawl', 'content_freshness', 'structural_seo', 'content_quality'];
}

export interface AuditConfig {
  urls?: string[];
  sitemapUrl?: string;
  crawlMode?: CrawlMode;
  engines?: AiEngine[];
  scenarios?: string[];
  competitors?: string[];
  socialPlatforms?: string[];
  brandStrategy?: Record<string, any>;
  sinceDate?: string;
  maxProposals?: number;
  /**
   * Phase C2: per-brand Website Audit triage override, merged by the API from
   * `brand.auditConfig` at audit-create time. Consumed by the Python CrawlAgent.
   */
  brandAuditConfig?: {
    includeUrls?: string[];
    excludeUrls?: string[];
  };
}

// --- Website audit page classification (Phase A triage) ---

/**
 * Deterministic page-type taxonomy assigned by the CrawlAgent based on URL path patterns.
 * Mirrors the Python `PageType` literal in `packages/agents/src/utils/page_classifier.py`.
 * High-value categories are kept by default; `utility` is always dropped; `legal`/`careers`/
 * `contact` are capped at 1 page each.
 */
export type PageType =
  | 'homepage'
  | 'product'
  | 'about'
  | 'pricing'
  | 'blog'
  | 'case_study'
  | 'docs'
  | 'legal'
  | 'careers'
  | 'contact'
  | 'utility'
  | 'other';

/** Reason a URL was kept or dropped by the Phase A triage step. */
export type TriageReason =
  | 'kept'
  | 'user_included' // matched brand's auditConfig.includeUrls — bypassed all filters
  | 'user_excluded' // matched brand's auditConfig.excludeUrls — dropped before any other filter
  | 'blocklisted'
  | 'duplicate_locale'
  | 'category_cap_exceeded'
  | 'cluster_cap_exceeded' // 5+ sibling `other`-class URLs sharing a path prefix — kept 2 reps
  | 'budget_exceeded';

/**
 * One entry in `triage.json`. Written by CrawlAgent, read by the API/frontend
 * for future "N pages filtrées (voir)" disclosure and override tooling.
 *
 * Note: the on-disk JSON uses snake_case (`page_type`, `canonical_url`) matching the
 * Python agent convention. API read paths should transform to this camelCase shape
 * before exposing to the frontend.
 */
export interface TriageDecision {
  url: string;
  pageType: PageType;
  kept: boolean;
  reason: TriageReason;
  /** Populated when `reason === 'duplicate_locale'` — the URL that was kept instead. */
  canonicalUrl?: string;
  /** Populated when `reason === 'cluster_cap_exceeded'` — the path prefix shared by the cluster. */
  clusterPrefix?: string;
}

/** One URL that passed triage but the HTTP fetch failed. */
export interface FetchFailure {
  url: string;
  /**
   * 'timeout' | 'network_error' | 'non_html' | `http_<status>` (e.g. 'http_403')
   * | 'budget_exhausted' (the crawl's wall-clock extraction budget ran out
   * before this URL was reached — see EXTRACTION_BUDGET_S in crawl.py).
   */
  reason: string;
}

/** Full triage log (S3 `tenants/<t>/audits/<a>/crawl/triage.json`). */
export interface TriageLog {
  auditId: string;
  tenantId: string;
  budget: number;
  targetLanguages: string[];
  discoveredCount: number;
  keptCount: number;
  /** URLs that actually made it into the crawl manifest. Missing on pre-fix audits — treat as undefined → fallback to decisions.filter(d => d.kept).length. */
  fetchedCount?: number;
  /** Per-URL fetch failure reasons for selected-but-unfetchable pages. */
  fetchFailures?: FetchFailure[];
  decisions: TriageDecision[];
  createdAt: string;
}

// --- Brand perception audit summary (AI engine queries) ---

export interface AuditSummary {
  citationCount: number;
  recommendationCount: number;
  overallScore: number;
  sentimentBreakdown: Record<Sentiment, number>;
  engineScores: Record<AiEngine, number>;
}

// --- Website audit summary (per-agent aggregates) ---

export interface WebsiteAuditSummary {
  overallGeoScore: number;
  pagesAnalyzed: number;
  crawl?: {
    pages_crawled: number;
    pages_discovered: number;
    /** Pages kept by Phase A triage (blocklist + locale dedup + category caps + budget). */
    pages_after_triage?: number;
    domain: string;
  };
  content_freshness?: {
    avg_freshness_score: number;
    pages_analyzed: number;
    pages_undated: number;
    pages_stale: number;
    pages_recently_updated: number;
    staleness_indicators_total: number;
  };
  structural_seo?: {
    avg_structural_score: number;
    pages_analyzed: number;
    critical_issues: number;
    warnings: number;
  };
  content_quality?: {
    avg_geo_readiness: number;
    pages_analyzed: number;
    signal_averages: Record<string, number>;
    weakest_signals: string[];
    avg_brand_alignment?: number;
    /**
     * Phase B per-page-type breakdown. Absent on pre-Phase-B audits. Keys are
     * PageType values; each carries count + the avg GEO readiness score for
     * that type. Enables "your blog is strong, your product pages need work"
     * surfacing in the UI without re-aggregating at read time.
     */
    page_type_averages?: Record<string, { count: number; avg_geo_readiness: number }>;
    /**
     * Phase D — business readability by AI. Written by the content_quality
     * agent at end of run, piggybacking on the same summary record to avoid
     * a second DDB row. See `BusinessReadability` for field semantics.
     */
    business_readability?: BusinessReadability;
  };
}

/**
 * Phase D — answers "does AI actually understand this business from the site?"
 *
 * Written once per audit by the content_quality agent after the per-page pass.
 * Runs one Haiku call over the homepage + product + about page content and
 * returns a natural-language synthesis + a confidence score + the list of
 * topics declared in Brand Strategy but absent from what the AI found on the
 * site. This is the "big picture" surface — complementary to per-page scoring.
 */
export interface BusinessReadability {
  /**
   * 1-paragraph natural-language synthesis of what an AI would understand the
   * business to be based on the site content. French.
   */
  synthesis: string;
  /**
   * 0-100 confidence that the synthesis is accurate. Low scores mean the site
   * is ambiguous, generic, or missing signal.
   */
  confidence: number;
  /**
   * Topics/keywords declared in Brand Strategy (authority keywords,
   * differentiators) that the AI could NOT find reflected on the site. Each
   * entry is the exact text from Brand Strategy — the gap is actionable.
   */
  missing_topics: string[];
  /**
   * Topics the AI picked up that aren't in the Brand Strategy — the site may
   * be drifting from stated positioning, or strategy may be outdated.
   */
  unexpected_topics: string[];
  /**
   * 2-4 actionable recommendations to improve AI readability. French.
   */
  recommendations: string[];
  /**
   * URLs the synthesis was based on. Usually homepage + 1-2 product + 1 about.
   */
  source_urls: string[];
}

export interface NewsWatchSummary {
  proposals_count: number;
  topics_covered: string[];
  since_date: string;
}

export interface Audit {
  tenantId: string;
  brandId?: string;
  auditId: string;
  type: AuditType;
  status: AuditStatus;
  config: AuditConfig;
  summary?: AuditSummary;
  websiteSummary?: WebsiteAuditSummary;
  earnedMediaSummary?: EarnedMediaAuditSummary;
  /** Legacy combined Brand Perception summary, only written by `type: 'brand'` audits. */
  brandPerceptionSummary?: BrandPerceptionSummary;
  brandNarrativeSummary?: BrandNarrativeSummary;
  brandAuthoritySummary?: BrandAuthoritySummary;
  brandReferralSummary?: BrandReferralSummary;
  brandComparisonSummary?: BrandComparisonSummary;
  newsWatchSummary?: NewsWatchSummary;
  agentStatuses?: AgentStatuses;
  domain?: string;
  reportS3Key?: string;
  createdAt: string;
  completedAt?: string;
  /** Prompt pipeline (lot G, written by the launch planner): the launch this axis audit belongs to. */
  launchId?: string;
  launchMode?: LaunchMode;
  launchDay?: string;
  /** `added` launches: hidden from the axis page (`'added_prompts'`, `brandTypePK` `#subset`). */
  scope?: 'added_prompts';
  /** Agency credit hold on this audit (Trigger or launch planner; the phase-checker settles it). */
  reservationId?: string;
  reservedCredits?: number;
  creditExact?: boolean;
  failureReason?: string;
}

// --- Brand perception audit result (AI engine queries) ---

export interface AuditResult {
  auditId: string;
  tenantId: string;
  engine: AiEngine;
  query: string;
  queryHash: string;
  response: string;
  cited: boolean;
  recommended: boolean;
  sentiment: Sentiment;
  score: number;
  isCompetitor: boolean;
  competitorName?: string;
  sourceUrl?: string;
  createdAt: string;
}

// --- Website audit per-page results ---

export interface FreshnessPageResult {
  agentType: 'content_freshness';
  pageUrl: string;
  urlHash: string;
  publishedDate: string | null;
  modifiedDate: string | null;
  ageDays: number | null;
  daysSinceUpdate: number | null;
  hasMachineReadableDates: boolean;
  stalenessIndicators: string[];
  freshnessScore: number;
  recommendations: string[];
}

export interface StructuralSeoPageResult {
  agentType: 'structural_seo';
  pageUrl: string;
  urlHash: string;
  dimensionScores: Record<string, number>;
  structuralScore: number;
  issues: Array<{
    dimension: string;
    severity: 'critical' | 'warning' | 'info';
    message: string;
    recommendation?: string;
    /** i18n key suffix for `auditDetail.issue.<code>.*` — absent on older audit rows, which fall back to `message`/`recommendation`. */
    code?: string;
    /** Interpolation values for the i18n message/recommendation (e.g. { length: 72 }). */
    params?: Record<string, string | number>;
  }>;
  recommendations: string[];
}

export interface BrandAlignmentDetail {
  score: number;
  keyword_coverage?: number | null;
  differentiator_coverage?: number | null;
  persona_relevance?: number | null;
  missing_keywords?: string[];
  missing_differentiators?: string[];
}

export interface ContentQualityPageResult {
  agentType: 'content_quality';
  pageUrl: string;
  urlHash: string;
  scores: Record<string, number>;
  geoReadiness: number;
  brandAlignment?: BrandAlignmentDetail;
  recommendations: string[];
}

export type WebsitePageResult =
  | FreshnessPageResult
  | StructuralSeoPageResult
  | ContentQualityPageResult;

// --- Earned Media audit types ---

export type EarnedMediaSourceType = 'press' | 'academic' | 'industry' | 'review' | 'wikipedia' | 'forum';
export type MediaType = 'earned' | 'owned' | 'social';

export type EarnedMediaFetchStatus = 'fetched' | 'blocked';

export interface EarnedMediaSourceResult {
  agentType: 'earned_media';
  sourceName: string;
  sourceUrl: string;
  sourceType: EarnedMediaSourceType;
  mediaType: MediaType;
  sentiment: Sentiment;
  brandMentioned: boolean;
  /** Optional. True when the brand is the PRIMARY subject of the page;
   *  false when it's referenced as one of N entries on a directory or
   *  comparison page. Absent on legacy rows — readers should default true.
   *  Drives the "mention seulement" chip in EarnedMediaV2Tabs. */
  brandPrimarySubject?: boolean;
  brandAccurate: boolean;
  /** Optional. `'fetched'` (default) means the page text was read and Haiku
   *  judged it. `'blocked'` means anti-bot/paywall stopped the fetch and the
   *  row was logged from the Tavily snippet only — `excerpt` is reliable
   *  but `sentiment` and `brandAccurate` are placeholders. Absent on legacy
   *  rows — readers should default to `'fetched'`. Drives the "lecture
   *  partielle" chip in EarnedMediaV2Tabs. */
  fetchStatus?: EarnedMediaFetchStatus;
  recency: string;
  relevanceScore: number;
  excerpt: string;
}

export interface EarnedMediaInaccuracy {
  agentType: 'earned_media';
  sourceName: string;
  sourceUrl: string;
  claim: string;
  reality: string;
  severity: 'critical' | 'minor';
}

export type WikipediaPresence = 'own_page' | 'mentioned_in_others' | 'wikidata_only' | 'absent';

export type AuthorityCategory =
  | 'trade_press'
  | 'national_business_press'
  | 'expert_blog'
  | 'review_site'
  | 'industry_report'
  | 'actionable_directory';

export interface AuthorityGap {
  domain: string;
  category?: AuthorityCategory | string;
  rationale?: string;
}

export interface EarnedMediaAuditSummary {
  overall_earned_media_score: number;
  wikipedia_exists: boolean;
  wikipedia_presence?: WikipediaPresence;
  wikipedia_quality_score: number;
  authority_mention_count: number;
  authority_source_types: Record<string, number>;
  earned_vs_owned_ratio: number | string;
  eeat_score: number;
  accuracy_score: number;
  inaccuracies_count: number;
  social_mention_count?: number;
  /** Third-party pages actually fetched + judged this run (the billable unit —
   *  1 credit / fetch). Written by the orchestrator's finalize; absent on legacy
   *  `agent` runs and pre-orchestrator audits. */
  fetch_count?: number;
  /** Bare domain strings (legacy). */
  coverage_gaps?: string[];
  /** Enriched gap details with category + rationale, when available. */
  coverage_gap_details?: AuthorityGap[];
  /** Flat recommendations — kept for legacy v1 readers. v2 audits also populate
   *  `recommendations_categorized` with the structured shape. */
  recommendations: string[];

  // --- v2 fields (orchestrator-only; absent on legacy `agent` runs) ---

  /** Brand envergure used to grade wikipedia_quality_score. Surfaced to UI
   *  so users see why a "no Wikipedia page" verdict isn't penalized for an SMB. */
  wikipedia_envergure_context?: 'local' | 'regional' | 'national' | 'international';

  /** Top missing-authority entries ranked by AI-leverage. Drives the
   *  Médias à conquérir tab. */
  missing_authorities?: MissingAuthority[];

  /** Cross-axis influence aggregated from BRAND#<id>/ENGINE_INFLUENCE# rows.
   *  Absent on cold start (no brand-perception history yet). */
  engine_influence?: EngineInfluenceBlock;

  /** Categorized recommendations replacing the flat bag. Empty buckets hidden in UI. */
  recommendations_categorized?: CategorizedRecommendations;

  /** Reddit / HN / public forum mentions. Same logical shape as
   *  EarnedMediaSourceResult but split out for the Conversations tab. */
  conversations?: ConversationMention[];
}

export interface MissingAuthority {
  domain: string;
  category: AuthorityCategory | string;
  rationale: string;
  /** 0-100 derived from authority list weight + engine_influence presence. */
  leverageScore: number;
  /** Per-axis citation counts when this domain appears in ENGINE_INFLUENCE rows. */
  citedByEngines?: {
    narrative: number;
    authority: number;
    referral: number;
  } | null;
  /** Competitors covered by this domain in your past brand-perception audits. */
  competitorsCovered?: string[];
  /** One-sentence pitch angle, generated by the synthesizer. French. */
  suggestedAngle?: string;
  effort?: 'low' | 'medium' | 'high';
}

export interface EngineInfluenceBlock {
  /** ISO date of the oldest ENGINE_INFLUENCE row read. */
  windowStart: string;
  windowEnd: string;
  /** Distinct (auditId, axis) tuples covered. Useful for UI: "5 cycles agrégés". */
  cyclesObserved: number;
  /** Top domains by total citations after filtering brand_owned/gov/other. */
  topDomains: EngineInfluenceDomain[];
  /** Top 10 domains with monthly citation counts (sparklines). */
  domainTrends: EngineInfluenceTrend[];
  /** Domains classified as brand_owned/gov/other and excluded from the
   *  influence panel. Surfaced in the UI so users can spot misclassifications.
   *  Top 20 by total citations. */
  excludedDomains?: { domain: string; classification: DomainClassification; total: number }[];
}

/**
 * Domains the Earned Media agent structurally never fetches (mirrors
 * `UNIVERSAL_BLOCKLIST_DOMAINS` in
 * packages/agent-earned-media/app/earned_media/_common.py — keep in sync).
 * These can still appear in `EngineInfluenceDomain.topDomains` (Brand
 * Perception doesn't exclude `social`), but Earned Media's logged sources
 * can NEVER contain them, so `isAcquired` would always read `false` even
 * when the brand has a real, positive presence there. `getEngineInfluence`
 * forces `isAcquired = null` ("not measured") for these instead of a
 * guaranteed-wrong "Non".
 */
export const EARNED_MEDIA_UNMEASURED_DOMAINS = new Set([
  'linkedin.com', 'facebook.com', 'twitter.com', 'x.com', 'instagram.com',
  'youtube.com', 'tiktok.com',
  'quora.com', 'stackoverflow.com', 'stackexchange.com',
  'wikipedia.org', 'wikidata.org',
]);

export interface EngineInfluenceDomain {
  domain: string;
  classification: DomainClassification;
  narrative: number;
  authority: number;
  referral: number;
  total: number;
  /**
   * True when this domain also appears in this audit's logged sources.
   * `null` means Earned Media structurally never measures this domain (see
   * `EARNED_MEDIA_UNMEASURED_DOMAINS`) — render as "Non mesuré", not "Non".
   */
  isAcquired: boolean | null;
}

export interface EngineInfluenceTrend {
  domain: string;
  /** Each entry: ISO YYYY-MM and total citations across axes that month. */
  monthly: { month: string; count: number }[];
}

/**
 * 11-category taxonomy. Designed to classify any domain across any industry/country
 * so each chip in the UI is meaningful and routing rules are unambiguous.
 *
 * Routing summary:
 *   - Médias acquis tab        : everything except `brand_owned`, `corporate`, `social`
 *   - Conversations & forums   : `social`, `review_platform` (the UGC half)
 *   - Influence sur moteurs IA : everything except `brand_owned`, `corporate`
 *   - Hidden everywhere        : `brand_owned`, `corporate`
 */
export type DomainClassification =
  | 'media' // Editorial press / signed articles / vertical or business publications
  | 'public_media' // Public-funded broadcasters and state press (BBC, Radio France, ARD, NHK, RAI…)
  | 'review_platform' // Claimable platforms whose primary value is user reviews (Glassdoor, Trustpilot, G2…)
  | 'job_board' // Recruitment platforms with claimable employer pages (Welcome to the Jungle, Indeed, Apec, JobTeaser…)
  | 'directory' // Claimable B2B listings, structured DBs, profile databases (Crunchbase, BuiltWith…)
  | 'industry_report' // Analyst firms, paid research, white-paper publishers (Gartner, Forrester, IDC…)
  | 'institution' // NGOs, certifying bodies, standards orgs (ISO, B-Corp, AFNOR…)
  | 'association' // Professional associations, chambers of commerce, trade unions
  | 'educational' // Universities, research labs, academic publishers (.edu and equivalents)
  | 'social' // Social networks, forums, UGC communities
  | 'gov' // Government, regulatory, .gov / .gouv / official agencies — INCLUDED in influence view (a city hall citing the brand IS earned media)
  | 'corporate' // Non-brand company sites (competitors, suppliers, partners) — hidden from views
  | 'brand_owned' // The audited brand's own domain — hidden from views
  | 'other'; // Last-resort fallback when classification fails — hidden from views

/**
 * Action type generated per classification — drives synthesizer's `suggestedAngle`
 * + `effort` calibration. Avoid recommending "publish a tribune" on a `gov` or
 * `educational` site.
 */
export const CLASSIFICATION_ACTION_TYPE: Record<DomainClassification, string> = {
  media: 'tribune, interview, communiqué, dossier éditorial',
  public_media: 'tribune, interview, communiqué, dossier éditorial',
  review_platform: 'revendiquer la fiche + collecte d\'avis utilisateurs',
  job_board: 'créer une page employeur + publier des offres ciblées',
  directory: 'revendiquer la fiche + tenir le profil à jour',
  industry_report: 'briefing analyste, soumission vendor',
  institution: 'certification, adhésion, contribution white paper',
  association: 'adhésion, intervention en événement, contribution',
  educational: 'étude de cas, partenariat de recherche, intervention académique',
  social: 'participation native (AMA, thought leadership employé)',
  gov: 'réponse à consultation, référencement marchés publics',
  corporate: '(non visé — non earned media)',
  brand_owned: '(votre domaine)',
  other: '(à reclassifier)',
};

/** Categories shown in Médias acquis tab. */
export const ACQUIRED_TAB_CLASSIFICATIONS: ReadonlySet<DomainClassification> = new Set([
  'media', 'public_media', 'review_platform', 'job_board', 'directory',
  'industry_report', 'institution', 'association', 'educational', 'gov',
]);

/** Categories EXCLUDED from Influence sur moteurs IA. */
export const INFLUENCE_EXCLUDED_CLASSIFICATIONS: ReadonlySet<DomainClassification> = new Set([
  'brand_owned', 'corporate',
]);

/** Categories allowed as `missing_authorities` targets. */
export const MISSING_AUTHORITY_CLASSIFICATIONS: ReadonlySet<DomainClassification> = new Set([
  'media', 'public_media', 'review_platform', 'job_board', 'directory',
  'industry_report', 'institution', 'association', 'educational',
]);

export interface CategorizedRecommendations {
  acquired_media: Recommendation[];
  missing_media: Recommendation[];
  precision: Recommendation[];
}

export interface Recommendation {
  text: string;
  severity: 'high' | 'medium' | 'low';
  actionable: boolean;
  /** Deep-link target (domain that the recommendation acts on, when applicable). */
  targetDomain?: string;
  /** Pre-drafted correction email — populated for `precision` items only. */
  draftEmail?: string;
}

export interface ConversationMention {
  platform: 'reddit' | 'hackernews' | 'forum';
  url: string;
  threadTitle: string;
  excerpt: string;
  sentiment: Sentiment;
  mentionCount: number;
  competitorsMentioned?: string[];
  engagement: { comments: number; upvotes?: number };
  suggestedAction?: string;
  /** How many brand-perception audit rows cite this URL in their `sources[]`. */
  citedInEngines?: number;
}

// --- Brand Perception audit types ---

export type BrandPerceptionAxis = 'narrative' | 'authority' | 'referral';
export type VisibilityLevel = 'L1' | 'L2' | 'L3' | 'L4' | 'L5';

export interface BrandPerceptionQueryResult {
  agentType: 'brand_perception';
  axis: BrandPerceptionAxis;
  level?: VisibilityLevel;
  /** Geographic facet (any axis) — filterable metadata, snapshot from the query row. */
  zone?: string;
  /** Language facet (ISO 639-1) — filterable metadata, snapshot from the query row. */
  language?: string;
  engine: AiEngine;
  query: string;
  /** Stable id of the editable query row (see BrandQuery). Missing for legacy `brand` audits. */
  queryId?: string;
  /** Snapshot of the query's textHash at run time — detects reworded queries. */
  queryTextHash?: string;
  response: string;
  cited: boolean;
  recommended: boolean;
  sentiment: Sentiment;
  score: number;
  narrativeAlignment?: string;
  /** Narrative-only: one-sentence French explanation of the alignment gap. Present going forward for weak/misaligned rows; absent on legacy audits. */
  alignmentReason?: string;
  /**
   * The engine refused the zone's structured location (Claude's web_search takes a fixed country
   * list) and answered without it: the zone was in the prompt only. Absent = normal (2026-11-03).
   */
  geoFallback?: { reason: 'location_unsupported'; country: string };
  competitorsMentioned?: string[];
  sources?: string[];
  /**
   * Per-source click-through URLs, POSITIONALLY aligned with `sources`
   * (index `i` is the link for `sources[i]`; `''` where there is none).
   * Gemini-only: its `sources` are `web.domain` labels, so the UI has nothing to
   * open — the link is a grounding-redirect wrapper the BROWSER follows on click
   * (we deliberately don't bulk-resolve them server-side; see
   * docs/features/brand-perception.md §4.3). Absent for every other engine, whose
   * `sources` already hold real URLs, and absent on rows written before 2026-07-29.
   */
  sourceLinks?: string[];
  brandIsSource?: boolean;
  /**
   * Cibles (Targets) SHADOW match — written by the BP worker beside `cited` while
   * the two verdicts are diffed (docs/plans/targets/, Lot 6 / D25). `cited` is
   * still the DISPLAYED verdict for this release; nothing should read these yet
   * except the diff script (scripts/targets-shadow-diff.py). All absent on rows
   * written before Lot 6 and on any row whose shadow call failed — a MISSING
   * `targetsCited` means "no shadow ran", not "not cited".
   *
   * ⚠ Since 2026-09-23 this is BROADER than `cited`, not a like-for-like shadow of
   * it: ANY target of the working brand (`ownerId === 'own'`) matched in the answer
   * BODY sets it, in any category — a branch by its street, a product by its URL.
   * `is_brand_cited` only knows names and aliases, so it can see none of those.
   * Two conditions did NOT widen: `ownerId` (a competitor's target never counts —
   * though that is a no-op until competitor ownership exists) and `via: 'text'` (a
   * target found only in a cited URL stays `brandIsSource`'s question). Tier is not
   * filtered, so a `probable` match counts — including an établissement matched by
   * its street next to the marque, which the matcher requires to be within the rung's
   * window AND in the same sentence. See docs/features/brand-perception.md §6.4 and
   * docs/features/targets.md §11.1.
   */
  targetsCited?: boolean;
  /** Total target matches before the `maxMatches` cap, across every category. */
  targetMatches?: number;
  /** Matched target ids, deduped, rank order. Load-bearing for erasure (D21). */
  targetIds?: string[];
  /** Manifest the match ran against, pinned at dispatch (D17) — makes the diff reproducible. */
  targetsHash?: string;
  /** The cap was hit, so `targetIds` is PARTIAL. */
  targetsTruncated?: boolean;
  /** Search-side warnings, e.g. `TARGETS_HASH_MISMATCH`. */
  targetsWarnings?: string[];
  /**
   * Matched terms found in the answer BODY, for the response highlight. Unlike the
   * fields above this one IS read by the UI: it is the only way a target that is
   * neither the brand name nor an alias (a sub-brand, a product, a competitor) can
   * be highlighted at all. Source-only matches are excluded — the term is in a
   * cited URL, not in the prose. Absent when nothing body-matched.
   */
  targetHighlights?: TargetHighlight[];
  /**
   * Matched citations, by index into `sources` — where the source-only matches
   * excluded above go. Also read by the UI: it colours the source chip so a target
   * cited but not named in the answer is still visible. Absent when no source
   * matched.
   */
  targetSourceHighlights?: TargetSourceHighlight[];
  /**
   * Per-category distinct target counts for own targets matched via text OR source
   * (2026-09-23). Excludes `marque` (always implicit when `targetsCited` is true).
   * Drives the category badges in the BP engine panel. Absent when no non-marque
   * target matched, and absent on rows written before 2026-09-23.
   *
   * Example: `[{ category: 'etablissement', count: 2 }, { category: 'produit', count: 1 }]`
   */
  targetCategories?: Array<{ category: string; count: number }>;
  /**
   * Citations of each matched target, per APEX domain — the BP dashboard's channel
   * input (`BPTOP.byDomain`, `BPTGTAGG.d_<YYYY>_<MM>`). ≤200 entries, written by
   * `targets_shadow._target_domains`.
   *
   * Resolved by the AGENT against the FULL citation list, deliberately NOT derivable
   * downstream: `sources` is capped at 20 while the matcher returns up to 200 matches,
   * so `targetSourceHighlights[].sourceIndex → sources[sourceIndex]` indexes past the
   * stored list for a target first cited by the 37th source and would resolve no
   * domain at all (plan §8 `targetDomains`).
   *
   * `n` counts DISTINCT citations. A citation whose apex domain is unusable is dropped,
   * so `Σ n` for a target is a LOWER bound on `targetFacets[].c` — never the reverse.
   * Absent when nothing source-matched, and on rows written before 2026-10-06.
   */
  targetDomains?: Array<{ targetId: string; domain: string; n: number }>;
  /**
   * Per-target facets of the matched targets — what makes the BP dashboard's
   * `byCategory` / `byTag` rollups EXACT over every cited target rather than a re-fold
   * of the day's `top[]` prefix. `targetCategories` above aggregates and drops the
   * target→facet link, and there is no cheap id→target lookup (the Cibles shards are
   * content-hash addressed, D6/§2.9), so the link has to be carried here.
   *
   * `c` = hits in this response (distinct cited sources + 1 if also named in the prose),
   * `ct` = category, `tg` = Cibles tag IDS (never names: a rename is a manifest write and
   * retroactive for free, a stored name would freeze the old label — D6b). ≤200 entries,
   * ≤20 tags each. Unlike `targetCategories` this INCLUDES `marque`.
   */
  targetFacets?: Array<{ targetId: string; c: number; ct?: string; tg?: number[] }>;
  /** Referral-only: 1-indexed rank of the brand in the recommendation list. */
  recommendedPosition?: number;
  /** Referral-only: total entries in the recommendation list, when knowable. */
  recommendedTotal?: number;
  /**
   * Referral-only: ordered list of every provider the engine recommended
   * (≤10 names). Feeds the Competitors matrix — each tracked competitor's
   * rank is `recommendedList.indexOf(name) + 1`. See competitors-redesign-plan §3.1.
   */
  recommendedList?: string[];
  /** Referral-only: 'high' for clearly-ranked lists, 'low' for hedged prose. */
  listConfidence?: 'high' | 'low';
  /**
   * The LLM judge produced no usable verdict for this row (Bedrock error, or
   * unparseable / wrong-schema output on both attempts — see `scorer.py`
   * `score_response`). When true, `sentiment`, `recommended` and
   * `narrativeAlignment` are NEUTRAL DEFAULTS, not judgements, and `score`
   * reflects only the deterministic signals (`cited`, `brandIsSource`).
   *
   * The UI must render "non évalué" rather than those defaults: they are
   * byte-identical to a genuine "cité mais pas recommandé" verdict, which is
   * how a failed judge shipped score 50 on an answer opening with "le meilleur
   * choix est Interflora" (referral/perplexity, 2026-09-29). Judge-derived
   * rollups skip the row (`tools._judged`); the deterministic citation rates
   * still count it.
   *
   * Absent on every successful row and on all rows written before 2026-09-29.
   */
  judgeFailed?: boolean;
  createdAt: string;
}

export interface ZoneVisibilityBreakdown {
  score: number;
  levels: Record<VisibilityLevel, {
    score: number;
    cited: number;
    total: number;
  }>;
}

export interface CompetitorMentionStats {
  /** Competitor display name (as stored in brand.competitors). */
  name: string;
  /** Total queries where the competitor was mentioned (any axis). */
  total: number;
  /** Subset of `total` where the competitor was cited in queries that also mentioned the brand. */
  cited_with_brand?: number;
  /** Breakdown per axis: counts of queries that mentioned this competitor. */
  by_axis: {
    narrative: number;
    authority: number;
    referral: number;
  };
}

export interface BrandPerceptionSummary {
  overall_brand_perception_score: number;
  narrative_score: number;
  authority_score: number;
  referral_score: number;
  engine_scores: Record<string, number>;
  narrative: {
    score: number;
    alignment: string;
    key_perception: string;
    pros: string[];
    cons: string[];
    query_count: number;
  };
  authority: {
    score: number;
    cited_topics: string[];
    missing_topics: string[];
    citation_rate: number;
    source_rate: number;
    query_count: number;
  };
  referral: {
    score: number;
    zones: Record<string, ZoneVisibilityBreakdown>;
    overall_citation_rate: number;
    overall_recommendation_rate: number;
  };
  sentiment_breakdown: Record<Sentiment, number>;
  suggested_scenarios: string[];
  suggested_keywords: string[];
  total_queries: number;
  recommendations: string[];
  /** Per-competitor aggregated mention counts across all logged queries. Keyed by name (lowercase). */
  competitor_mentions?: Record<string, CompetitorMentionStats>;
}

// --- Split Brand Perception summaries (per-axis) ---
//
// One per axis audit type. The legacy BrandPerceptionSummary remains for historical
// `brand` audits; new per-axis audits write only their axis-specific summary.

/**
 * One per-prompt rollup inside a Brand Perception axis summary.
 *
 * Powers the matrix view: each entry contributes one cell in the
 * (prompt × audit) grid. The frontend joins entries across audits on
 * `queryTextHash` so reworded prompts become distinct series and archived
 * prompts still render in older columns.
 *
 * Older audits (pre-queryScores migration) won't carry this field — the
 * frontend treats absent entries as "no data" cells.
 */
/**
 * One competitor's cell for one prompt in the Competitors matrix.
 * `state` semantics differ per axis (see CompetitorMatrix):
 *   - referral:  'position' (ranked, carries `position`) | 'cited' | 'absent'
 *   - authority: 'cited' | 'absent'
 *   - narrative: 'cocited' | 'absent'
 */
export interface CompetitorCell {
  state: 'position' | 'cited' | 'cocited' | 'absent';
  /** 1-indexed best rank across engines — referral 'position' cells only. */
  position?: number;
}

export interface CompetitorMatrixPrompt {
  queryId: string;
  queryTextHash: string;
  queryText: string;
  level?: string | null;
  language?: string | null;
  /** Keyed by competitor original name → cell. */
  cells: Record<string, CompetitorCell>;
}

export interface CompetitorMatrixFooter {
  presentOn: number;
  promptCount: number;
  /** Referral-only: prompts where the competitor was recommended (ranked). */
  recommendedOn?: number;
  /** Referral-only: mean rank across prompts where ranked. */
  avgPosition?: number;
}

/**
 * Per-prompt competitor cells + footer rollups, embedded in each axis
 * AGENT_DONE summary under `competitor_matrix`. Class A of the Competitors
 * feature — pure aggregation over audit rows, zero marginal LLM calls.
 * `competitors` is the tracked-name list this matrix was computed against
 * (cells/footer keyed by these names).
 */
export interface CompetitorMatrix {
  competitors: string[];
  prompts: CompetitorMatrixPrompt[];
  footer: Record<string, CompetitorMatrixFooter>;
}

/**
 * An untracked brand the AI engines recommend (Competitors feature §4). Detected
 * from referral `recommendedList` entries that are neither the brand nor already
 * tracked. Surfaced as "+ Suivre" rows. Zero marginal LLM — pure aggregation.
 */
export interface SuggestedCompetitor {
  name: string;
  /** Prompts (this audit) whose recommendation list named this brand. */
  citedIn: number;
  /** Total prompts in the audit — denominator for "cité dans N/M". */
  promptCount: number;
  /** SURE domain captured from a cited source host matching the name (no search
   *  needed). Absent when no cited source matched — the API resolves it via search. */
  domain?: string;
  /** `cited` = domain taken from a cited source (certain). Absent → unresolved. */
  domainConfidence?: 'cited';
}

export interface QueryScoreEntry {
  queryId: string;
  /** sha1 of the normalized prompt text — stable across prompt edits. */
  queryTextHash: string;
  /** Snapshot of the prompt text at audit time (may differ from current `BrandQuery.text`). */
  queryText: string;
  /** 0-100 per-prompt score (axis-specific aggregation). ABSENT on an unscored
   *  classified entry (F step 7b): never read as `0`. */
  score?: number;
  /** Number of distinct engines that returned a row for this prompt. */
  engineCount: number;
  // Narrative-only:
  alignment?: 'strong' | 'partial' | 'weak' | 'misaligned' | string;
  sentiment?: Sentiment;
  // Authority-only:
  citationRate?: number;
  // Referral-only:
  citedRate?: number;
  recommendedRate?: number;
  level?: string;
  /**
   * Best (lowest = highest-ranked) position the brand achieved across all
   * engines for this prompt. Only present when at least one engine returned
   * a numeric `recommendedPosition`. UI surfaces this as "#1" / "#3" next
   * to the prompt row.
   */
  bestPosition?: number;
  // F step 8b-bis (classified narrative / comparison entries; absent on legacy ones):
  /** `compare_v2` (rank among the compared brands) replaced `compare_v1` (won/tie/lost) on 2026-11-02. */
  scoreRule?: 'rubric_v0' | 'image_v1' | 'compare_v1' | 'compare_v2' | string;
  scoreKind?: ScoreKind;
  preference?: 'won' | 'tie' | 'lost' | 'not_named' | string;
  /** Source audit when every entry row has one; else `aidByEngine`. */
  aid?: string;
  aidByEngine?: Record<string, string>;
  olderRows?: number;
}

/**
 * Optional richer recommendation shape with impact/effort metadata.
 *
 * The legacy `recommendations: string[]` field is preserved on every summary
 * for backward compat with pre-existing audits + the report exporter. New
 * audits also populate `recommendations_v2` when the synthesizer emits the
 * structured form. The frontend prefers v2 when present.
 */
export interface BrandAxisRecommendation {
  title: string;
  detail?: string;
  impact: 'high' | 'medium' | 'low';
  effort: 'high' | 'medium' | 'low';
  /** queryTextHash this reco is anchored on, when applicable. */
  queryTextHash?: string;
}

/**
 * Dashboard rollups (Class A) embedded in every Brand Perception axis summary.
 *
 * Deterministic, zero-LLM, computed at audit finalize from the rows already in
 * memory and capped at write time so the embedded audit-row summary stays small
 * at 500 prompts. Read by the dashboard via the embedded summary — no read-time
 * fan-out over result rows. Absent on legacy audits → UI shows an empty state.
 * See docs/dashboard-redesign-plan.md (Phase 2).
 */
export interface LanguageScoreEntry {
  /** ISO 639-1 code (fr, en, es, …). */
  language: string;
  /** Mean of per-prompt scores for this language (0-100). ABSENT on an
   *  `image_v1`-only language (F step 8b-bis). */
  score?: number;
  /** Number of prompts answered in this language. */
  promptCount: number;
  /** Narrative, F step 8b-bis: the scored `image_v1` rows' mean / count, beside `score`. */
  score_image_v1?: number;
  n_image_v1?: number;
}

export interface CitedPageEntry {
  /** Brand-owned page URL cited as a source by an AI engine. */
  url: string;
  /** Number of cited rows (across engines) that referenced this page. */
  count: number;
}

export interface DashboardAxisRollups {
  /** Mean score per prompt language (≤5, sorted desc). */
  language_scores?: LanguageScoreEntry[];
  /** Count of rows where the brand was cited, keyed by engine (≤7 keys). */
  engine_citation_counts?: Record<string, number>;
  /** Brand-owned pages cited as sources, ranked by frequency (top 20). */
  cited_pages?: CitedPageEntry[];
}

/**
 * 00b DQ8.4 / DQ9: a new-pipeline summary keeps `query_scores` and the per-prompt
 * `competitor_matrix.prompts` in chunk items, not embedded — read them through
 * `perPromptChunkKeys` / `assemblePerPrompt`. Absent on legacy audits.
 */
export interface PerPromptShardFields {
  /** Chunk counts: `QSCORE#0..qscore-1`, `CMATRIX#0..cmatrix-1` under `AUDIT#<auditId>` (audit-results). */
  perPromptShards?: { qscore: number; cmatrix: number };
  /** Entries a chunk family had to leave out (one entry over the chunk budget). */
  omittedEntries?: { qscore?: number; cmatrix?: number };
}

export interface BrandNarrativeSummary extends DashboardAxisRollups, PerPromptShardFields {
  /** 0-100, derived from LLM-assigned alignment + pros/cons coverage. ABSENT when
   *  no row is scored (F step 7b). */
  score?: number;
  /** ABSENT when no scored classified row carries one (F step 7b, no fallback). */
  alignment?: 'strong' | 'partial' | 'weak' | 'misaligned' | string;
  key_perception: string;
  pros: string[];
  cons: string[];
  query_count: number;
  engine_scores: Record<string, number>;
  sentiment_breakdown: Record<Sentiment, number>;
  suggested_keywords?: string[];
  suggested_scenarios?: string[];
  recommendations: string[];
  recommendations_v2?: BrandAxisRecommendation[];
  query_scores?: QueryScoreEntry[];
  /** Per-prompt competitor cells + footer rollups (Class A). Absent on legacy audits. */
  competitor_matrix?: CompetitorMatrix;
  /**
   * F step 7b: scored rows per scoring rule, present only when ≥ 1 `image_v1` row.
   * `score` then mixes both rules (C1); `ruleMixKey` tells two headlines comparable.
   */
  scoreRuleCounts?: { rubric_v0?: number; image_v1?: number };
  /** Mean over the scored `image_v1` rows; absent when there is none. */
  score_image_v1?: number;
}

export interface BrandAuthoritySummary extends DashboardAxisRollups, PerPromptShardFields {
  /** 0-100, = domain_citation_rate × 100 (fully deterministic). */
  score: number;
  /** Share of queries where the brand's domain appeared in sources[]. */
  domain_citation_rate: number;
  /** Share of queries where the brand's name/alias appeared in the response text. */
  response_text_mention_rate: number;
  /** Topics (clustered post-hoc by LLM) where the brand was cited as a source. */
  cited_topics: string[];
  /** Topics where the brand was NOT cited — gap signal. */
  missing_topics: string[];
  avg_sources_per_response: number;
  query_count: number;
  engine_scores: Record<string, number>;
  recommendations: string[];
  recommendations_v2?: BrandAxisRecommendation[];
  query_scores?: QueryScoreEntry[];
  /** Per-prompt competitor cells + footer rollups (Class A). Absent on legacy audits. */
  competitor_matrix?: CompetitorMatrix;
}

export interface BrandReferralSummary extends DashboardAxisRollups, PerPromptShardFields {
  /** 0-100, zone-averaged and level-weighted. */
  score: number;
  zones: Record<string, ZoneVisibilityBreakdown>;
  overall_citation_rate: number;
  overall_recommendation_rate: number;
  query_count: number;
  engine_scores: Record<string, number>;
  sentiment_breakdown: Record<Sentiment, number>;
  /** Per-competitor aggregated mention counts across referral queries. Keyed by lowercase name. */
  competitor_mentions?: Record<string, CompetitorMentionStats>;
  recommendations: string[];
  recommendations_v2?: BrandAxisRecommendation[];
  query_scores?: QueryScoreEntry[];
  /** Per-prompt competitor cells + footer rollups (Class A). Absent on legacy audits. */
  competitor_matrix?: CompetitorMatrix;
  /** Untracked brands the engines recommend (referral-only, §4). */
  suggested_competitors?: SuggestedCompetitor[];
}

export interface BrandComparisonSummary extends DashboardAxisRollups, PerPromptShardFields {
  /** 0-100, `compare_v1` (brand-perception.md §3.6). ABSENT when no row is scored, never 0. */
  score?: number;
  alignment?: 'strong' | 'partial' | 'weak' | 'misaligned' | string;
  /** Dominant brand preference verdict across judged rows. */
  preference?: 'won' | 'tie' | 'lost' | 'not_named' | string;
  /** Preference counts over scored rows (F step 7b). */
  preference_breakdown?: { won: number; tie: number; lost: number; not_named: number };
  /** Sums over scored rows; ABSENT when no row carries them. */
  criteriaAddressed?: number;
  criteriaWon?: number;
  query_count: number;
  engine_scores: Record<string, number>;
  sentiment_breakdown: Record<Sentiment, number>;
  recommendations?: string[];
  recommendations_v2?: BrandAxisRecommendation[];
  query_scores?: QueryScoreEntry[];
  /** Per-prompt competitor cells + footer rollups. Absent on legacy audits. */
  competitor_matrix?: CompetitorMatrix;
}

/**
 * Response shape for `GET /brands/:brandId/axis-matrix?axis=&limit=`.
 *
 * Built from the last N AGENT_DONE summaries for a single Brand Perception
 * axis (newest-first). Powers the matrix view: prompts as rows, audits as
 * columns. Per-prompt cells live on `audits[i].queryScores`.
 */
export interface AxisMatrixAuditPoint {
  auditId: string;
  completedAt: string;
  /** Overall axis score (0-100) for the audit. */
  score: number;
  /** Per-prompt rollups; absent on legacy audits without queryScores. */
  queryScores: QueryScoreEntry[];
  /** Per-prompt competitor cells + footer; null on legacy/no-competitor audits. */
  competitorMatrix?: CompetitorMatrix | null;
  /** Untracked-brand suggestions (referral-only); absent on other axes/legacy. */
  suggestedCompetitors?: SuggestedCompetitor[];
}

export interface AxisMatrixResponse {
  brandId: string;
  axis: BrandAxisAuditType;
  audits: AxisMatrixAuditPoint[];
}
